import { memo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CalendarDays, CalendarClock, CalendarCheck, AlertCircle, CalendarX } from "lucide-react";
import IMAGES from "../../../../../constant/images";

// Icon mapping for different stat types
const STAT_ICONS = {
  total: IMAGES.completed_icon,
  upcoming: IMAGES.practitioner_icon,
  completed: IMAGES.completed_icon,
  incomplete: IMAGES.practitioner_icon,
  cancelled: IMAGES.completed_icon
};

// Default stats structure for mapping API data to display
const getStatsFromApiData = (statsData) => {
  if (!statsData) return [];

  const statsConfig = [
    { id: 'total', dataKey: 'totalAppointments', title: 'Total' },
    { id: 'upcoming', dataKey: 'upcomingAppointments', title: 'Upcoming' },
    { id: 'completed', dataKey: 'completedAppointments', title: 'Completed' },
    { id: 'incomplete', dataKey: 'incompleteAppointments', title: 'Incomplete' },
    { id: 'cancelled', dataKey: 'cancelledAppointments', title: 'Cancelled' }
  ];

  return statsConfig.map(config => ({
    id: config.id,
    icon: STAT_ICONS[config.id],
    value: statsData[config.dataKey] || 0,
    title: config.title
  }));
};

// Presentational lucide icon + tone per stat id (falls back to the image icon)
const STAT_LUCIDE = {
  total: { Icon: CalendarDays, tone: 'bg-[#EFF6FF] text-[#2563EB]' },
  upcoming: { Icon: CalendarClock, tone: 'bg-[#EFF6FF] text-[#2563EB]' },
  completed: { Icon: CalendarCheck, tone: 'bg-[#F0FDFA] text-[#0D9488]' },
  incomplete: { Icon: AlertCircle, tone: 'bg-[#FFF7ED] text-[#EA580C]' },
  cancelled: { Icon: CalendarX, tone: 'bg-[#FFF1F2] text-[#E11D48]' }
};

// Reusable class constants (stacked layout, same as the practitioner Quick Stats cards)
const CLASSES = {
  CARD: 'flex flex-col rounded-2xl border-[1px] border-solid p-3 w-full min-w-0 bg-white shadow-sm cursor-pointer hover:shadow-md transition-all duration-300 ease-in-out',
  CARD_DEFAULT: 'border-[#E2E8F0]',
  CARD_SELECTED: 'border-[#2563EB] ring-2 ring-[#BFDBFE]',
  ICON_CONTAINER: 'w-[38px] h-[38px] bg-[#EFF6FF] rounded-xl flex items-center justify-center flex-shrink-0',
  ICON: 'w-5 h-5',
  VALUE: 'text-[26px] leading-tight font-bold text-[#171717] font-poppins break-all',
  TITLE: 'mt-0.5 text-[13px] uppercase tracking-wide font-medium text-[#64748B] font-poppins'
};

const StatCard = memo(({ icon, value, title, id, onClick, isSelected }) => {
  const lucide = STAT_LUCIDE[id];
  return (
  <div
    className={`${CLASSES.CARD} ${isSelected ? CLASSES.CARD_SELECTED : CLASSES.CARD_DEFAULT}`}
    onClick={() => onClick && onClick(id)}
    role="button"
    tabIndex={0}
    aria-pressed={isSelected}
    aria-label={`Filter by ${title.toLowerCase()} appointments`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick && onClick(id);
      }
    }}
  >
    {/* Icon tile */}
    <div className="flex items-start justify-between mb-1.5">
      <div className={lucide ? `${CLASSES.ICON_CONTAINER} ${lucide.tone}` : CLASSES.ICON_CONTAINER}>
        {lucide ? (
          <lucide.Icon size={18} />
        ) : (
          <img
            src={icon}
            alt={`${title} icon`}
            className={CLASSES.ICON}
            loading="lazy"
          />
        )}
      </div>
    </div>

    {/* Text Section */}
    <div className={CLASSES.VALUE} aria-label={`${value} ${title.toLowerCase()}`}>
      {value.toLocaleString()}
    </div>
    <div className={CLASSES.TITLE}>{title}</div>
  </div>
  );
});

StatCard.displayName = 'StatCard';

StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool
};

function AppointmentsCounter({ stats, onStatClick, selectedStatId }) {
  // Get stats data from Redux
  const { data: apiStatsData, loading, error } = useSelector(state => state.appointments.stats);

  // Use provided stats prop, or derive from Redux data, or show loading/error states
  const displayStats = stats || getStatsFromApiData(apiStatsData);
  
  if (loading) {
    const LoadingSkeleton = () => (
      <div className="flex flex-col rounded-2xl border-[1px] border-solid border-[#E2E8F0] p-3 w-full bg-white shadow-sm animate-pulse">
        <div className="w-[38px] h-[38px] bg-[#F1F5F9] rounded-xl flex-shrink-0 mb-1.5"></div>
        <div className="h-6 md:h-7 bg-[#F1F5F9] rounded w-16 mb-1"></div>
        <div className="h-3 md:h-4 bg-[#F1F5F9] rounded w-20"></div>
      </div>
    );

    return (
      <div className="mb-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
          {Array.from({ length: 5 }, (_, i) => (
            <LoadingSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-4">
        <div className="text-[#DC2626] text-sm font-poppins text-center p-4">
          Error loading appointment statistics: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 w-full">
      {/* Row: Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
        {displayStats.map((stat) => (
          <StatCard
            key={stat.id}
            id={stat.id}
            icon={stat.icon}
            value={stat.value}
            title={stat.title}
            onClick={onStatClick}
            isSelected={selectedStatId === stat.id}
          />
        ))}
      </div>
    </div>
  );
}

AppointmentsCounter.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    })
  ),
  onStatClick: PropTypes.func,
  selectedStatId: PropTypes.string
};

export default AppointmentsCounter;
